// Desktop shortcuts: two-column grid on the left. Icons can be dragged to a
// new cell (positions persist); anything that no longer fits after a resize
// flows into the next free cell, and "Auto-arrange" restores the default order.

import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AppIcon } from '../brand/brand';
import { local } from '../lib/storage';
import { tokenLabel, useCoordinator } from '../state/coordinator';
import { desktopShortcuts, type Shortcut } from './apps';
import { openApp, useWm } from './wm';

const CELL_W = 100;
const CELL_H = 104;
const ORIGIN = 18;

type Cell = { c: number; r: number };
type Placement = Record<string, Cell>;

export function layoutIcons(keys: string[], saved: Placement, rows: number, cols: number): Placement {
  const out: Placement = {};
  const taken = new Set<string>();
  const k = (c: Cell) => `${c.c}:${c.r}`;
  for (const key of keys) {
    const s = saved[key];
    if (s && Number.isInteger(s.r) && Number.isInteger(s.c) && s.r >= 0 && s.c >= 0 && s.r < rows && s.c < cols && !taken.has(k(s))) {
      out[key] = s;
      taken.add(k(s));
    }
  }
  let i = 0;
  const columns = Math.max(1,Math.min(2,cols));
  const slot = (n:number):Cell => ({c:n%columns+Math.floor(n/(rows*columns))*columns,r:Math.floor(n/columns)%rows});
  for (const key of keys) {
    if (out[key]) continue;
    while (taken.has(k(slot(i)))) i++;
    out[key] = slot(i);
    taken.add(k(out[key]));
  }
  return out;
}

function DesktopIconsImpl() {
  const config = useCoordinator((s) => s.config);
  const area = useWm((s) => s.area);
  const phone = useWm((s) => s.phone);
  const shortcuts = useMemo(() => desktopShortcuts(tokenLabel(config)), [config]);
  const keys = shortcuts.map((s) => s.key);
  const [saved, setSaved] = useState<Placement>(() => local.get<Placement>('icons.v1', {}));
  const [selected, setSelected] = useState<string | null>(null);
  const rows = Math.max(1, Math.min(6,Math.floor((area.h - ORIGIN) / CELL_H)));
  const cols = Math.max(1, Math.floor((area.w - ORIGIN) / CELL_W));
  const place = useMemo(() => layoutIcons(keys, saved, rows, cols), [keys.join(), saved, rows, cols]); // eslint-disable-line react-hooks/exhaustive-deps
  const btns = useRef<Record<string, HTMLButtonElement | null>>({});
  const coarse = typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;

  useEffect(() => {
    const clear = (e: PointerEvent) => {
      if (!(e.target as HTMLElement).closest?.('.dicon')) setSelected(null);
    };
    window.addEventListener('pointerdown', clear);
    const auto = () => {
      local.del('icons.v1');
      setSaved({});
    };
    window.addEventListener('kumio:auto-arrange', auto);
    return () => {
      window.removeEventListener('pointerdown', clear);
      window.removeEventListener('kumio:auto-arrange', auto);
    };
  }, []);

  const launch = useCallback((s: Shortcut) => openApp(s.app, s.arg ?? null), []);

  const onPointerDown = (s: Shortcut) => (e: React.PointerEvent<HTMLButtonElement>) => {
    if (e.button !== 0) return;
    setSelected(s.key);
    if (phone) return;
    const el = e.currentTarget;
    const sx = e.clientX;
    const sy = e.clientY;
    const start = place[s.key];
    let dragging = false;
    el.setPointerCapture(e.pointerId);
    const move = (ev: PointerEvent) => {
      const dx = ev.clientX - sx;
      const dy = ev.clientY - sy;
      if (!dragging && Math.abs(dx) + Math.abs(dy) < 6) return;
      dragging = true;
      el.classList.add('dragging');
      el.style.left = ORIGIN + start.c * CELL_W + dx + 'px';
      el.style.top = ORIGIN + start.r * CELL_H + dy + 'px';
    };
    const up = (ev: PointerEvent) => {
      el.removeEventListener('pointermove', move);
      el.removeEventListener('pointerup', up);
      el.removeEventListener('pointercancel', up);
      el.classList.remove('dragging');
      if (!dragging) return;
      el.dataset.justDragged = '1';
      setTimeout(() => delete el.dataset.justDragged, 0);
      const c = Math.min(cols - 1, Math.max(0, Math.round((start.c * CELL_W + ev.clientX - sx) / CELL_W)));
      const r = Math.min(rows - 1, Math.max(0, Math.round((start.r * CELL_H + ev.clientY - sy) / CELL_H)));
      const next: Placement = { ...place };
      const occupant = Object.keys(next).find((k) => k !== s.key && next[k].c === c && next[k].r === r);
      if (occupant) next[occupant] = start; // swap
      next[s.key] = { c, r };
      local.set('icons.v1', next);
      setSaved(next);
      // React re-applies left/top from state; clear the inline drag position.
      el.style.left = ORIGIN + c * CELL_W + 'px';
      el.style.top = ORIGIN + r * CELL_H + 'px';
    };
    el.addEventListener('pointermove', move);
    el.addEventListener('pointerup', up);
    el.addEventListener('pointercancel', up);
  };

  const onKeyDown = (s: Shortcut, idx: number) => (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      launch(s);
      return;
    }
    const cur = place[s.key];
    const delta: Record<string, [number, number]> = { ArrowDown: [0, 1], ArrowUp: [0, -1], ArrowRight: [1, 0], ArrowLeft: [-1, 0] };
    const d = delta[e.key];
    if (!d) return;
    e.preventDefault();
    const target = shortcuts.find((o) => place[o.key].c === cur.c + d[0] && place[o.key].r === cur.r + d[1]);
    const next = target ?? shortcuts[(idx + (d[0] + d[1] > 0 ? 1 : -1) + shortcuts.length) % shortcuts.length];
    setSelected(next.key);
    btns.current[next.key]?.focus();
  };

  return (
    <nav className="desktop-icons" aria-label="Desktop shortcuts">
      {shortcuts.map((s, i) => {
        const p = place[s.key];
        return (
          <button
            key={s.key}
            ref={(el) => (btns.current[s.key] = el)}
            className={'dicon' + (selected === s.key ? ' selected' : '')}
            style={phone ? { animationDelay: i * 18 + 'ms' } : { left: ORIGIN + p.c * CELL_W, top: ORIGIN + p.r * CELL_H, animationDelay: i * 18 + 'ms' }}
            data-shortcut={s.key}
            title={s.label}
            aria-label={`${s.label}. Double-click or press Enter to open.`}
            onPointerDown={onPointerDown(s)}
            onClick={(e) => {
              if ((e.currentTarget as HTMLElement).dataset.justDragged) return;
              if (phone || coarse || e.detail === 0) launch(s);
            }}
            onDoubleClick={() => !phone && !coarse && launch(s)}
            onKeyDown={onKeyDown(s, i)}
          >
            <span className="dicon-art">
              <AppIcon name={s.icon} size={76} />
            </span>
            <span className="dicon-label">{s.label}</span>
          </button>
        );
      })}
    </nav>
  );
}

export const DesktopIcons = memo(DesktopIconsImpl);
